import { useContext, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { DialogActions, DialogContent, Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import { numberWithCommas } from "../../../../utils/utility";
import { LocaleContext } from "../../../../routing/LangRouter";
import { connect } from "react-redux";
import moment from "moment";

//import redux store
import { saveSelectedTicket, saveTicketSelectedFilters } from "../../../../store/actions";

//import general components
import { BaseButton } from "../../../common/base-button/base-button";
import BaseTabs from "../../../common/base-tabs/base-tabs";
import BaseDialog from "../../../common/base-dialog/base-dialog";

//import view components
import IntFlightPriceDetails from "./int-flight-price-details";
import IntFlightInformation from "./int-flight-information";
import FareRules from "./int-flight-fare";
import BaggageRules from "./int-flight-baggage";
import AvailabiltyModal from "../../availability/availabilty-modal";

//import constants
import { ItineraryContext } from "../../../../constants/layouts/itinerary-layout";

//import styles
import "./int-flight-details-modal.scss";

function IntFlightDetailsModal({ dispatch, open, setOpen, flightItem, typeDetails }) {
    //general variables
    const navigate = useNavigate();
    const { routes, setRoutes } = useContext(ItineraryContext);
    const [searchParams, setSearchParams] = useSearchParams();
    const { locale } = useContext(LocaleContext);
    const { t } = useTranslation();

    //sessionStorage variables
    const generalInformation = JSON.parse(sessionStorage.getItem("generalInformation")) || {};
    const { isItinerary } = JSON.parse(sessionStorage.getItem("generalInformation")) || {};
    const itineraryInformation = JSON.parse(sessionStorage.getItem("itineraryInformation"));
    const intFlightInformation = JSON.parse(sessionStorage.getItem("intFlightInformation"));

    const oldDetails = flightItem;
    const [intFlightDetails, setIntFlightDetails] = useState("");
    useEffect(() => {
        setIntFlightDetails(flightItem);
    }, [flightItem]);

    //handle opening modal
    const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);
    const [isReserveModalOpen, setIsReserveModalOpen] = useState(false);

    //handle close modal
    const handleClose = () => {
        setActiveTab({ departure: true, return: false, rules: false });
        setBaggageInfo(null);
        setFareRules([]);
        setOpen(false);
        navigate(-1);
    };

    //open the modal with query params
    useEffect(() => {
        if (open) {
            setSearchParams({ action: "modal" });
        } else {
            searchParams.delete("action");
            setSearchParams(searchParams);
        }
    }, [open]);

    //define variables for tabs
    const [activeTab, setActiveTab] = useState({
        departure: true,
        return: false,
        rules: false,
    });

    //list of tabs
    const tabList = [
        {
            type: "departure",
            title: !!intFlightDetails?.return_segments?.length ? t("departureFlight") : t("flight"),
            activeTab: activeTab?.departure,
            disable: !intFlightDetails?.segments?.length,
        },
        {
            type: "return",
            title: t("returnFlight"),
            activeTab: activeTab?.return,
            disable: !intFlightDetails?.return_segments?.length,
        },
        {
            type: "rules",
            title: t("rules"),
            activeTab: activeTab?.rules,
        },
    ];

    //menu default value
    useEffect(() => {
        setActiveTab({ [typeDetails]: true });
    }, [typeDetails]);

    //handle tab change
    const handleTabChange = type => {
        setActiveTab({ [type]: true });
    };

    //update the itinerary date and time based on the selected flight
    const updateItineraryDateTime = selectedIntFlight => {
        let hasUpdates = false;

        // Update departure timing if segments exist
        if (selectedIntFlight?.segments?.length > 0) {
            const firstSegment = selectedIntFlight.segments[0];
            if (firstSegment?.arrival_date && firstSegment?.destination_time) {
                const departureDatetime = moment(`${firstSegment.arrival_date} ${firstSegment.destination_time.split(":", 1).join(":00")}`)
                    .add(4, "hours")
                    .format()
                    .split("+")[0];
                const [departureDate, departureTime] = departureDatetime.split("T");
                itineraryInformation.departureDate = departureDate;
                itineraryInformation.firstDayTime = departureTime;
                hasUpdates = true;
            }
        }

        // Update return timing if return_segments exist
        if (selectedIntFlight?.return_segments?.length > 0) {
            const lastReturnSegment = selectedIntFlight.return_segments[0];
            if (lastReturnSegment?.departure_date && lastReturnSegment?.departure_time) {
                const returnDatetime = moment(`${lastReturnSegment.departure_date} ${lastReturnSegment.departure_time.split(":", 1).join(":00")}`)
                    .add(-4, "hours")
                    .format()
                    .split("+")[0];
                const [returnDate, returnTime] = returnDatetime.split("T");
                itineraryInformation.returnDate = returnDate;
                itineraryInformation.lastDayTime = returnTime;
                hasUpdates = true;
            }
        }

        // Set itineraryGenerate to true if we made any updates
        if (hasUpdates) {
            itineraryInformation.itineraryGenerate = true;
        }

        sessionStorage.setItem("itineraryInformation", JSON.stringify(itineraryInformation));
    };

    const navigateNextPage = () => {
        const isAccommodation = generalInformation?.isAccommodation;
        setRoutes([...routes, `/${locale}/itinerary/${isAccommodation ? "hotel" : "itinerary-page"}`]);
        sessionStorage.setItem("visitedRoutes", JSON.stringify([...routes, `/${locale}/itinerary/${isAccommodation ? "hotel" : "itinerary-page"}`]));
        navigate(`/${locale}/itinerary/${isAccommodation ? "hotel" : "itinerary-page"}`);
    };

    //handle filter cancellation
    const resetFilters = () => {
        dispatch(saveTicketSelectedFilters(null));
    };

    //handle selected ticket
    const handleSelectedTicket = () => {
        resetFilters();
        sessionStorage.setItem("selectedIntFlight", JSON.stringify(intFlightDetails));
        if (isItinerary) {
            updateItineraryDateTime(intFlightDetails);
            navigateNextPage();
        } else {
            navigate(`/${locale}/int-flight-passengers`);
        }
    };

    // States for managing baggage information and fare rules
    const [baggageInfo, setBaggageInfo] = useState(null);
    const [fareRules, setFareRules] = useState([]);

    const handleAvailabilityAction = type => {
        sessionStorage.setItem("selectedIntFlight", JSON.stringify(intFlightDetails));
        dispatch(saveSelectedTicket(intFlightDetails));

        if (type === "notification") {
            setIsNotificationModalOpen(true);
        } else if (type === "reserve") {
            setIsReserveModalOpen(true);
        }
    };

    return (
        <>
            <BaseDialog title={t("flightDetails")} open={open} handleClose={handleClose} maxWidth="lg" top="0" icon="close">
                <DialogContent dividers={true} alignitems="flex-start">
                    <BaseTabs handleTabChange={handleTabChange} list={tabList} style={{ top: "-1rem" }} />
                    <Grid container item xs={12} rowGap={{ xs: 2, md: 5 }} mt={{ xs: 0, md: 5 }}>
                        {intFlightDetails?.[activeTab.departure ? "segments" : activeTab?.return ? "return_segments" : null]?.map(
                            (segment, index) => {
                                return (
                                    <Grid key={Math.random()} container item xs={12} justifyContent="flex-end" rowGap={{ xs: 2, md: 5 }}>
                                        <IntFlightInformation intFlightDetails={intFlightDetails} segment={segment} index={index} />
                                    </Grid>
                                );
                            },
                        )}

                        {activeTab?.rules && (
                            <>
                                <span className="int-flight-details-modal__prices">{t("baggageRules")}:</span>
                                <BaggageRules
                                    baggageInfo={baggageInfo}
                                    setBaggageInfo={setBaggageInfo}
                                    fareSourceCode={intFlightDetails?.fare_source_code}
                                />
                                <span className="int-flight-details-modal__prices">{t("visaRouteRules")}:</span>
                                <FareRules fareRules={fareRules} setFareRules={setFareRules} fareSourceCode={intFlightDetails?.fare_source_code} />
                            </>
                        )}

                        <IntFlightPriceDetails
                            intFlightInformation={intFlightInformation}
                            oldDetails={oldDetails}
                            intFlightDetails={intFlightDetails}
                        />
                    </Grid>
                </DialogContent>

                <DialogActions className="int-flight-details-modal__total-price">
                    {intFlightDetails?.capacity === 0 ? (
                        <Grid container item justifyContent="space-between" columnGap={1}>
                            <Grid container item xs={5}>
                                <BaseButton
                                    text={t("General.Notify When Available")}
                                    color="primary"
                                    size="large"
                                    radius="radius-10"
                                    handleClick={() => handleAvailabilityAction("notification")}
                                />
                            </Grid>
                            <Grid container item xs={5}>
                                <BaseButton
                                    text={t("General.Reserve When Available")}
                                    color="primary"
                                    size="large"
                                    radius="radius-10"
                                    handleClick={() => handleAvailabilityAction("reserve")}
                                />
                            </Grid>
                        </Grid>
                    ) : (
                        <Grid container item xs={12} justifyContent="space-between" alignItems="center">
                            <span>
                                {t("total")}: {numberWithCommas(intFlightDetails?.fares?.total_price)} {t("currency")}
                            </span>
                            <Grid container item xs={4} md={2}>
                                <BaseButton
                                    text={t("purchase")}
                                    color="primary"
                                    radius="radius-10"
                                    size="small"
                                    handleClick={() => handleSelectedTicket(intFlightDetails)}
                                />
                            </Grid>
                        </Grid>
                    )}
                </DialogActions>
            </BaseDialog>
            <AvailabiltyModal
                flightItem={intFlightDetails}
                isModalOpen={isNotificationModalOpen}
                setIsModalOpen={setIsNotificationModalOpen}
                title={t("General.Notify When Available")}
                modalType="notification"
                pageUrl={`/${locale}/flight-availability`}
            />
            <AvailabiltyModal
                flightItem={intFlightDetails}
                isModalOpen={isReserveModalOpen}
                setIsModalOpen={setIsReserveModalOpen}
                title={t("General.Reserve When Available")}
                modalType="reserve"
                pageUrl={`/${locale}/flight-automatic-reserve`}
            />
        </>
    );
}

export default connect()(IntFlightDetailsModal);

//handle revalidate

// const [revalidateLoading, setRevalidateLoading] = useState(false);
// const [validateRequest, setValidateRequest] = useState(false);

// const handleRevalidate = async ticket => {
//     try {
//         setRevalidateLoading(true);
//         let data = {
//             fare_source_code: ticket?.fare_source_code,
//         };
//         const response = await axios.post(`${BASE_URL}/api/v2/reserve/foreign/flight/revalidate/`, data);
//         setRevalidateLoading(false);
//         setIntFlightDetails(response?.data.data.flight_data);
//         if (response?.data?.data?.flight_data?.fares?.total_price === oldDetails?.fares?.total_price) {
//             sessionStorage.setItem("selectedIntFlight", JSON.stringify(response?.data.data.flight_data));

//             if (isItinerary) {
//                 updateItineraryDateTime(response?.data.data.flight_data);
//                 navigateNextPage();
//             } else {
//                 navigate(`/${locale}/int-flight-passengers`);
//             }
//         } else setValidateRequest(true);
//     } catch (error) {
//         setRevalidateLoading(false);
//         sessionStorage.removeItem("selectedIntFlight");
//         setTimeout(() => {
//             handleClose();
//             toast.error(t("Toast Messages.Invalid Ticket Selection"), { toastId: "custom-id-yes" });
//         }, 500);
//     }
// };
